import { useEffect, useRef } from 'react';

export default function Hero() {
  const compassRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const compass = compassRef.current;
    if (!compass) return;

    let rotation = 0;
    const animate = () => {
      rotation += 0.3;
      compass.style.transform = `rotate(${rotation}deg)`;
      requestAnimationFrame(animate);
    };

    const animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contacto');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const servicesSection = document.getElementById('servicios');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="bg-white pt-24 pb-0 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <h1 className="text-5xl md:text-6xl font-bold text-primary leading-tight">
              Donde tu marca encuentra una nueva{' '}
              <span className="text-accent">dirección</span>
            </h1>
            <p className="text-xl text-secondary leading-relaxed">
              Transformamos empresas B2B con estrategias de marketing digital que
              generan resultados medibles. Acompañamos tu crecimiento con método,
              claridad y visión comercial.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={scrollToContact}
                className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Coordinar reunión
              </button>
              <button
                onClick={scrollToServices}
                className="bg-white border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Conocer servicios
              </button>
            </div>
          </div>

          {/* Right - Animated Compass */}
          <div className="flex items-center justify-center">
            <div className="relative w-full max-w-md aspect-square">
              {/* Outer Circle with gradient */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#6B7F39]/20 to-[#6B7F39]/5 animate-pulse"></div>

              {/* Middle Circle */}
              <div className="absolute inset-8 rounded-full bg-white shadow-2xl flex items-center justify-center">
                {/* Compass Container */}
                <div className="relative w-full h-full flex items-center justify-center">
                  {/* Cardinal Points */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="absolute top-8 text-2xl font-bold text-[#6B7F39]">
                      N
                    </span>
                    <span className="absolute bottom-8 text-2xl font-bold text-gray-400">
                      S
                    </span>
                    <span className="absolute right-8 text-2xl font-bold text-gray-400">
                      E
                    </span>
                    <span className="absolute left-8 text-2xl font-bold text-gray-400">
                      O
                    </span>
                  </div>

                  {/* Rotating Compass Needle */}
                  <div
                    ref={compassRef}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div className="relative w-2/3 h-2/3">
                      {/* North Needle (Green) */}
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[120px] border-b-[#6B7F39]"></div>

                      {/* South Needle (Gray) */}
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[120px] border-t-gray-300"></div>
                    </div>
                  </div>

                  {/* Center Circle */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-[#6B7F39] rounded-full shadow-lg flex items-center justify-center z-10">
                    <div className="w-8 h-8 bg-white rounded-full"></div>
                  </div>

                  {/* Degree Marks */}
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute top-1/2 left-1/2 w-1 h-full origin-bottom"
                      style={{
                        transform: `translate(-50%, -100%) rotate(${i *
                          30}deg)`,
                      }}
                    >
                    </div>
                  ))}
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#6B7F39]/10 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#6B7F39]/10 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
